import React, { useState, useEffect } from 'react';
import { FileText, Building, Package, Save, Book, BookUp, BookA, BookCheckIcon, BookCopy, BookCheck, PlusCircle, Upload, PlusCircleIcon, Plus, Users, Download, Edit, Trash2, Mail, Send, Settings, Calendar } from "lucide-react";
import { Link, useParams } from 'react-router-dom';
import PageTitle from "../../layouts/PageTitle";
import { getApiUrl } from '../../../utils/ApiUtils';
import axios from '../../../services/AxiosInstance';
import swal from "sweetalert";
import { Card, Tabs, Tab, Form, Button, Badge, Table } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import FileDropZone from "../SharedComponents/FileDropZone";
import { useInvoiceSettings } from '../../../context/InvoiceSettingsContext';
import { useQuoteSettings } from '../../../context/QuoteSettingsContext';
import { useFeatures } from '../../../context/FeatureContext';

import RoleModal from './UserRoleModal';
import InviteUserModal from './InviteUserModal';
import SieVerificationModal from '../Modals/SieVerificationModal';
import SieExportModal from '../Modals/SieExportModal';

import CreateFiscalYearModal from '../Accounting/CreateFiscalYearModal';

import { toast } from 'sonner';

const CompanySettings = () => {
  const { companyId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const history = useHistory();

  const { invoiceSettings, isLoading: isLoadingInvoiceSettings, updateInvoiceSetting, saveInvoiceSettings, changedSections: invoiceChangedSections } = useInvoiceSettings();
  const { quoteSettings, isLoading: isLoadingQuoteSettings, updateQuoteSetting, saveQuoteSettings, changedSections: quoteChangedSections } = useQuoteSettings();
  const { features, isLoading: isLoadingFeatures, updateFeature, saveFeatures } = useFeatures();

  // State for company details
  const [companyDetails, setCompanyDetails] = useState({
    companyName: "",
    registrationNumber: "",
    vatNumber: "",
    companyEmail: "",
    companyPhone: "",
    bankgiro: "",
    plusgiro: "",
    contactEmail: "",
    contactPhone: "",
    contactPerson: "",
    address: "",
    city: "",
    postalCode: "",
    country: ""
  });

  const [fiscalYears, setFiscalYears] = useState([]);
  const [files, setFiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [associatedUsers, setAssociatedUsers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [allRoles, setAllRoles] = useState([]);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [accountPlan, setAccountPlan] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  // New state for SIE verification
  const [showSieVerificationModal, setShowSieVerificationModal] = useState(false);
  const [sieVerificationData, setSieVerificationData] = useState(null);
  const [isImporting, setIsImporting] = useState(false);

  const [showSieExportModal, setShowSieExportModal] = useState(false);

  const [showCreateFiscalYearModal, setShowCreateFiscalYearModal] = useState(false);

  const navigateToFiscalYearDetails = (fiscalYearId) => () => {
    history.push(`accounting/fiscal-years/${fiscalYearId}`);
  };

  const navigateToTemplateEditor = (templateId = 'new') => {
    history.push(`/${companyId}/templates/${templateId}`);
  };

  // Fetch all settings on component mount
  useEffect(() => {
    fetchAllSettings();
  }, [companyId]);

  const fetchAllSettings = async () => {
    setIsLoading(true);
    try {
      // Fetch company details
      await fetchCompanyDetails();

      // Fetch fiscal years
      await fetchFiscalYears();
      await fetchTemplates();
      await fetchAssociatedUsers();
      await fetchPendingInvitations();
      await fetchAllRoles();
      await fetchAccountPlan();

      console.log('All settings fetched successfully');
      setIsLoading(false);
    } catch (error) {
      toast.error("Failed to load settings");
      setIsLoading(false);
    }
  };

  const fetchCompanyDetails = async () => {
    try {
      const url = getApiUrl(`${companyId}/companydetails`);
      const { data } = await axios.get(url, { withCredentials: true });

      setCompanyDetails({
        companyName: data.company_name || "",
        registrationNumber: data.registration_number || "",
        vatNumber: data.vat_number || "",
        companyEmail: data.company_email || "",
        companyPhone: data.company_phone || "",
        bankgiro: data.bankgirot || "",
        plusgiro: data.plusgirot || "",
        contactEmail: data.contact_email || "",
        contactPhone: data.contact_phone || "",
        contactPerson: data.contact_person || "",
        address: data.address || "",
        city: data.city || "",
        postalCode: data.postal_code || "",
        country: data.country || ""
      });
    } catch (error) {
      console.error('Error getting company details:', error);
      throw error;
    }
  };

  const fetchFiscalYears = async () => {
    try {
      const url = getApiUrl(`${companyId}/accounting/fiscalyears`);
      const { data } = await axios.get(url, { withCredentials: true });
      setFiscalYears(data.fiscalYears);
    } catch (error) {
      console.error('Error getting fiscal years:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const url = getApiUrl(`${companyId}/templates`);
      const { data } = await axios.get(url, { withCredentials: true });
      setTemplates(data.templates);
    } catch (error) {
      console.error('Error getting templates:', error);
    }
  };

  const fetchAssociatedUsers = async () => {
    try {
      const url = getApiUrl(`${companyId}/users`);
      const { data } = await axios.get(url, { withCredentials: true });
      setAssociatedUsers(data.users);
    } catch (error) {
      console.error('Error getting associated users:', error);
    }
  };

  const fetchPendingInvitations = async () => {
    try {
      const url = getApiUrl(`${companyId}/invitations`);
      const { data } = await axios.get(url, { withCredentials: true });
      setPendingInvitations(data.invitations);
    } catch (error) {
      console.error('Error getting invitations:', error);
    }
  };

  const fetchAllRoles = async () => {
    try {
      const url = getApiUrl(`${companyId}/roles`);
      const { data } = await axios.get(url, { withCredentials: true });
      setAllRoles(data.roles);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  const fetchAccountPlan = async () => {
    try {
      const url = getApiUrl(`${companyId}/accounting/accounts`);
      const { data } = await axios.get(url, { withCredentials: true });
      setAccountPlan(data.accounts);
    } catch (error) {
      console.error('Error getting account plan:', error);
    }
  };

  const handleFiscalYearAction = async (fiscalYearId, action) => {
    const actionApiUrl = getApiUrl(`${companyId}/accounting/fiscalyears/${fiscalYearId}/${action}`);
    try {
      const { data } = await axios.put(actionApiUrl, {}, { withCredentials: true });
      if (data.success) {
        toast.success(data.message);
        fetchFiscalYears();
      }
    } catch (error) {
      toast.error(`Failed to ${action} fiscal year`);
    }
  };

  // const handleUpload = async () => {
  //   if (files.length === 0) {
  //     swal("Error", "Please select a file to upload", "error");
  //     return;
  //   }
  //   setIsLoading(true);

  //   const formData = new FormData();
  //   formData.append('file', files[0]);

  //   try {
  //     const url = getApiUrl(`${companyId}/verify-se-file`);
  //     const { data } = await axios.post(url, formData, {
  //       withCredentials: true,
  //       headers: { 'Content-Type': 'multipart/form-data' },
  //     });

  //     console.log('Verification response:', data);

  //     if (data.success) {
  //       // Ensure the verification data has the expected structure
  //       const structuredData = {
  //         ...data,
  //         verification: data.verification || {
  //           validation: {
  //             isValid: false,
  //             errors: [],
  //             warnings: [],
  //             summary: {
  //               totalErrors: 0,
  //               totalWarnings: 0,
  //               verificationsChecked: 0,
  //               accountsChecked: 0
  //             }
  //           },
  //           preview: data.preview || {}
  //         }
  //       };

  //       // Show verification modal
  //       setSieVerificationData(structuredData);
  //       setShowSieVerificationModal(true);
  //     } else {
  //       swal("Error", data.message || "Failed to verify the file", "error");
  //     }
  //   } catch (error) {
  //     console.error('Verification error:', error);
  //     swal("Error", "Failed to verify the file", "error");
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };

  // // Function to handle confirmed import
  // const handleConfirmImport = async () => {
  //   setIsImporting(true);

  //   const formData = new FormData();
  //   formData.append('file', files[0]);
  //   formData.append('fileId', sieVerificationData.fileId);
  //   formData.append('importOptions', JSON.stringify({}));

  //   try {
  //     const url = getApiUrl(`${companyId}/import-se-file-confirmed`);
  //     const { data } = await axios.post(url, formData, {
  //       withCredentials: true,
  //       headers: { 'Content-Type': 'multipart/form-data' },
  //     });

  //     if (data.success) {
  //       swal("Success", "SIE-filen har importerats framgångsrikt!", "success");
  //       setShowSieVerificationModal(false);
  //       setSieVerificationData(null);
  //       setFiles([]);
  //       fetchFiscalYears();
  //     } else {
  //       swal("Error", data.message || "Failed to import the file", "error");
  //     }
  //   } catch (error) {
  //     console.error('Import error:', error);
  //     swal("Error", "Failed to import the file", "error");
  //   } finally {
  //     setIsImporting(false);
  //   }
  // };

  // Add these functions to handle the enhanced workflow:

  const handleUpload = async () => {
    if (files.length === 0) {
      toast.error("Please select a file to upload");
      return;
    }
    setIsLoading(true);

    const formData = new FormData();
    formData.append('file', files[0]);

    try {
      const url = getApiUrl(`${companyId}/accounting/sie/verify`);
      const { data } = await axios.post(url, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (data.success) {
        // Store the file data for later import
        const structuredData = {
          ...data,
          fileId: data.fileId,
          originalFile: files[0], // Keep reference to original file
          verification: data.validation,
          preview: data.preview
        };

        setSieVerificationData(structuredData);
        setShowSieVerificationModal(true);
      } else {
        toast.error(data.message || "Failed to verify the file");
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || "Failed to verify the file";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmImport = async (fiscalYearSettings) => {
    setIsImporting(true);

    const formData = new FormData();
    formData.append('file', sieVerificationData.originalFile);
    formData.append('fileId', sieVerificationData.fileId);

    // Add fiscal year settings to the import options
    const importOptions = {
      fiscalYearSettings: fiscalYearSettings
    };
    formData.append('importOptions', JSON.stringify(importOptions));

    console.log('Importing with fiscal year settings:', fiscalYearSettings);

    try {
      const url = getApiUrl(`${companyId}/accounting/sie/import`);
      const { data } = await axios.post(url, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      if (data.success) {
        setShowSieVerificationModal(false);
        setSieVerificationData(null);
        setFiles([]);

        toast.success("SIE file imported successfully with configured settings");

        // Refresh data
        await fetchAllSettings();
      } else {
        toast.error(data.message || "Failed to import the file");
      }
    } catch (error) {
      console.error('Import error:', error);
      const errorMessage = error.response?.data?.message || "Failed to import the file";
      toast.error(errorMessage);
    } finally {
      setIsImporting(false);
    }
  };

  // Function to handle cancel
  const handleCancelImport = () => {
    setShowSieVerificationModal(false);
    setSieVerificationData(null);
    setFiles([]);
  };

  const handleOpenExportModal = () => {
    setShowSieExportModal(true);
  };

  const handleCloseExportModal = () => {
    setShowSieExportModal(false);
  };

  const handleCloseModal = () => {
    setShowCreateFiscalYearModal(false);
  };

  const handleSaveFiscalYear = async (fiscalYearData) => {
    try {
      const url = getApiUrl(`${companyId}/accounting/fiscalyears`);
      const { data } = await axios.post(url, fiscalYearData, { withCredentials: true });

      if (data.success) {
        toast.success("Räkenskapsår skapades framgångsrikt");
        setShowCreateFiscalYearModal(false);
        // Refresh the fiscal years list
        await fetchFiscalYears();
      } else {
        toast.error(data.message || "Kunde inte skapa räkenskapsår");
      }
    } catch (error) {
      console.error('Error creating fiscal year:', error);
      const errorMessage = error.response?.data?.message || "Kunde inte skapa räkenskapsår";
      toast.error(errorMessage);
    }
  };

  const saveInvoiceNumbering = async () => {
    try {
      console.log('Saving invoice numbering - optimized to save only invoiceNumbering section');
      await saveInvoiceSettings('invoiceNumbering');
      toast.success("Invoice numbering settings saved successfully");
    } catch (error) {
      toast.error("Failed to save invoice numbering settings");
    }
  };

  const savePaymentTerms = async () => {
    try {
      await saveInvoiceSettings('defaultTerms');
      toast.success("Payment terms saved successfully");
    } catch (error) {
      toast.error("Failed to save payment terms");
    }
  };

  const togglePaymentMethod = (method) => {
    updateInvoiceSetting('paymentMethods', {
      [method]: !invoiceSettings.paymentMethods[method]
    });
  };

  const savePaymentMethods = async () => {
    try {
      await saveInvoiceSettings('paymentMethods');
      toast.success("Payment methods saved successfully");
    } catch (error) {
      toast.error("Failed to save payment methods");
    }
  };

  const toggleDeliveryMethod = (method) => {
    updateInvoiceSetting('deliveryChannels', {
      [method]: !invoiceSettings.deliveryChannels[method]
    });
  };

  const saveDeliveryMethods = async () => {
    try {
      await saveInvoiceSettings('deliveryChannels');
      toast.success("Delivery methods saved successfully");
    } catch (error) {
      toast.error("Failed to save delivery methods");
    }
  };

  const saveCompanyDetails = async () => {
    try {
      const url = getApiUrl(`${companyId}/companydetails`);
      await axios.put(url, companyDetails, { withCredentials: true });
      toast.success("Company details saved successfully");
    } catch (error) {
      toast.error("Failed to save company details");
    }
  };

  const saveQuoteNumbering = async () => {
    try {
      await saveQuoteSettings('quoteNumbering');
      toast.success("Quote numbering settings saved successfully");
    } catch (error) {
      toast.error("Failed to save quote numbering settings");
    }
  };

  const saveBothNumberingSettings = async () => {
    try {
      await Promise.all([
        saveInvoiceSettings('invoiceNumbering'),
        saveQuoteSettings('quoteNumbering')
      ]);
      toast.success("All numbering settings saved successfully");
    } catch (error) {
      toast.error("Failed to save some numbering settings");
    }
  };

  const saveTaxSettings = async () => {
    try {
      await saveInvoiceSettings('taxSettings');
      toast.success("Tax settings saved successfully");
    } catch (error) {
      toast.error("Failed to save tax settings");
    }
  };

  const saveTemplateSettings = async () => {
    try {
      await saveInvoiceSettings('template');
      toast.success("Template settings saved successfully");
    } catch (error) {
      toast.error("Failed to save template settings");
    }
  };

  const handleOpenRoleModal = (user) => {
    setSelectedUser(user);
    setShowRoleModal(true);
  };

  const handleCloseRoleModal = () => {
    setShowRoleModal(false);
    setSelectedUser(null);
  };

  const handleSaveRoles = async (userId, roleIds) => {
    try {
      const url = getApiUrl(`${companyId}/users/${userId}/roles`);
      await axios.put(url, { roleIds }, { withCredentials: true });
      toast.success("User roles updated successfully");
      fetchAssociatedUsers();
      handleCloseRoleModal();
    } catch (error) {
      toast.error("Failed to update user roles");
    }
  };

  const canRemoveUser = (userId) => {
    // Find the user being removed
    const userToRemove = associatedUsers.find(user => user.user_id === userId);

    // Check if the user is an admin
    const isAdmin = userToRemove?.roles.includes('Admin');

    if (!isAdmin) {
      return { canRemove: true };
    }

    // Count total admins
    const adminCount = associatedUsers.filter(user =>
      user.roles.includes('Admin')
    ).length;

    // If this is the last admin, prevent removal
    if (adminCount <= 1) {
      return {
        canRemove: false,
        reason: 'Du kan inte ta bort den sista administratören. Det måste finnas minst en admin i företaget.'
      };
    }

    return { canRemove: true };
  };

  const handleRemoveUser = async (userId) => {
    try {
      // Check if user can be removed
      const { canRemove, reason } = canRemoveUser(userId);

      if (!canRemove) {
        toast.warning(reason);
        return;
      }

      const result = await swal({
        title: "Är du säker?",
        text: "Detta kommer att ta bort användaren från företaget",
        icon: "warning",
        buttons: ["Avbryt", "Ta bort"],
        dangerMode: true,
      });

      if (result) {
        const url = getApiUrl(`${companyId}/users/${userId}`);
        await axios.delete(url, { withCredentials: true });
        toast.success("Användaren har tagits bort");
        fetchAssociatedUsers();
      }
    } catch (error) {
      toast.error("Kunde inte ta bort användaren");
    }
  };

  const handleInviteUser = async (email, roles) => {
    try {

      const url = getApiUrl(`${companyId}/invitations`);
      await axios.post(url, { email, roles }, { withCredentials: true });
      toast.success("Invitation sent successfully");
      fetchPendingInvitations();
      setShowInviteModal(false);
    } catch (error) {
      toast.error("Failed to send invitation");
    }
  };

  const handleResendInvitation = async (invitationId) => {
    try {
      const url = getApiUrl(`${companyId}/invitations/${invitationId}/resend`);
      await axios.post(url, {}, { withCredentials: true });
      toast.success("Invitation resent successfully");
    } catch (error) {
      toast.error("Failed to resend invitation");
    }
  };

  const handleDeleteInvitation = async (invitationId) => {
    try {
      const result = await swal({
        title: "Are you sure?",
        text: "This will delete the invitation",
        icon: "warning",
        buttons: true,
        dangerMode: true,
      });

      if (result) {
        const url = getApiUrl(`${companyId}/invitations/${invitationId}`);
        await axios.delete(url, { withCredentials: true });
        toast.success("Invitation deleted successfully");
        fetchPendingInvitations();
      }
    } catch (error) {
      toast.error("Failed to delete invitation");
    }
  };

  if (isLoading || isLoadingInvoiceSettings || isLoadingQuoteSettings || isLoadingFeatures) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{`
.modern-card {
  background: white;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  margin-bottom: 1.5rem;
  overflow: hidden;
}

.modern-card-header {
  background: #f9fafb;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modern-card-body {
  padding: 1.5rem;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.section-subtitle {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
  margin-top: 0.25rem;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

/* Discrete table styling */
.modern-table-container {
  background: #f9fafb;
  border-radius: 8px;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
}

.modern-table {
  margin: 0;
  background: white;
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.modern-table thead th {
  background: #f3f4f6;
  border: none;
  color: #4b5563;
  font-weight: 600;
  font-size: 0.875rem;
  padding: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
}

.modern-table tbody td {
  border: none;
  padding: 0.875rem;
  border-bottom: 1px solid #f3f4f6;
  vertical-align: middle;
}

.modern-table tbody tr:last-child td {
  border-bottom: none;
}

.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clickable-row:hover {
  background: #f9fafb !important;
}

.table-cell-content {
  display: flex;
  align-items: center;
}

.fiscal-year-badge {
  background: #3b82f6;
  color: white;
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  font-weight: 500;
  font-size: 0.875rem;
}

.status-badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.625rem;
  border-radius: 4px;
  font-weight: 500;
}

.method-text, .vat-period-text {
  font-size: 0.875rem;
  color: #6b7280;
}

.table-actions {
  display: flex;
  gap: 0.375rem;
}

.action-button {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
  border-radius: 4px;
  font-weight: 500;
}

/* Discrete Import/Export Grid */
.import-export-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

.import-export-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s ease;
}

.import-export-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-1px);
}

// .import-card {
//   border-left: 3px solid #3b82f6;
// }

// .export-card {
//   border-left: 3px solid #10b981;
// }

.card-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
}

.import-icon {
  background: #eff6ff;
  color: #3b82f6;
}

.export-icon {
  background: #ecfdf5;
  color: #10b981;
}

.card-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
}

.card-description {
  color: #6b7280;
  font-size: 0.875rem;
  margin-bottom: 1.25rem;
  line-height: 1.4;
}

.file-drop-container {
  margin-bottom: 1.25rem;
}

.import-button {
  font-weight: 500;
}

.export-options {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.export-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem;
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  transition: all 0.2s ease;
}

.export-option:hover:not(.disabled) {
  border-color: #d1d5db;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.export-option.disabled {
  opacity: 0.6;
}

.export-option-info {
  flex: 1;
}

.export-format {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.export-format h6 {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
}

.export-format small {
  color: #6b7280;
  font-size: 0.75rem;
}

.export-button {
  flex-shrink: 0;
}

/* Discrete Templates Grid */
.templates-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.25rem;
}

.template-card {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.25rem;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 0.875rem;
}

.template-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.template-icon {
  width: 40px;
  height: 40px;
  background: #eff6ff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3b82f6;
}

.template-content {
  flex: 1;
}

.template-name {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.375rem 0;
}

.template-description {
  color: #6b7280;
  font-size: 0.8125rem;
  margin: 0;
  line-height: 1.4;
}

.template-actions {
  display: flex;
  justify-content: flex-end;
}

.template-edit-btn {
  font-size: 0.8125rem;
  padding: 0.375rem 0.75rem;
}

/* Discrete Account Plan Table */
.account-table .account-number {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-weight: 600;
  color: #374151;
  background: #f3f4f6;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  display: inline-block;
  font-size: 0.8125rem;
}

.account-name {
  font-weight: 500;
  color: #4b5563;
}

.account-type-badge {
  background: #dbeafe;
  color: #1e40af;
  padding: 0.25rem 0.625rem;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 500;
}

.custom-account-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.625rem;
}

/* Discrete Empty State */
.empty-state {
  text-align: center;
  padding: 2.5rem 1rem;
  color: #6b7280;
}

.empty-icon {
  color: #d1d5db;
  margin-bottom: 0.875rem;
}

.empty-state h4 {
  color: #4b5563;
  margin-bottom: 0.5rem;
  font-size: 1rem;
  font-weight: 600;
}

.empty-state p {
  margin: 0;
  font-size: 0.875rem;
}

/* Responsive Design */
@media (max-width: 768px) {
  .import-export-grid {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
  
  .templates-grid {
    grid-template-columns: 1fr;
  }
  
  .modern-table-container {
    padding: 0.5rem;
  }
  
  .modern-table thead th,
  .modern-table tbody td {
    padding: 0.625rem 0.5rem;
  }
  
  .import-export-card {
    padding: 1.25rem;
  }
  
  .modern-card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.875rem;
  }
}

@media (max-width: 640px) {
  .export-option {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .export-button {
    align-self: flex-end;
  }
}

/* Keep all your existing styles for other components */
.user-section {
  margin-bottom: 3rem;
}

.user-section:last-child {
  margin-bottom: 0;
}

.section-header {
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.section-heading {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.user-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 1.5rem;
}

.user-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.3s ease;
  position: relative;
}

.user-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.user-avatar {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  margin-bottom: 1rem;
}

.user-info {
  margin-bottom: 1rem;
}

.user-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.25rem 0;
}

.user-email {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0 0 0.25rem 0;
}

.user-phone {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0 0 0.75rem 0;
}

.user-roles {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.role-badge {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1e40af;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  border: 1px solid #93c5fd;
}

.role-badge.pending {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
  border-color: #fbbf24;
}

.user-actions {
  display: flex;
  gap: 0.5rem;
  position: absolute;
  top: 1rem;
  right: 1rem;
}

.action-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  cursor: pointer;
}

.action-btn.edit {
  background: #f1f5f9;
  color: #3b82f6;
}

.action-btn.edit:hover {
  background: #3b82f6;
  color: white;
}

.action-btn.delete {
  background: #fef2f2;
  color: #ef4444;
}

.action-btn.delete:hover {
  background: #ef4444;
  color: white;
}

.action-btn.resend {
  background: #f0f9ff;
  color: #0ea5e9;
}

.action-btn.resend:hover {
  background: #0ea5e9;
  color: white;
}

.invitation-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.invitation-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.3s ease;
}

.invitation-card:hover {
  border-color: #f59e0b;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.invitation-avatar {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.invitation-avatar.pending {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
}

.invitation-info {
  flex: 1;
}

.invitation-email {
  font-size: 1rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
}

.invitation-meta {
  display: flex;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.meta-item {
  color: #64748b;
  font-size: 0.75rem;
}

.invitation-roles {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.invitation-actions {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.features-grid {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.feature-category {
  margin-bottom: 2rem;
}

.category-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e2e8f0;
}

.category-title {
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.premium-badge {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.feature-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
  gap: 1.5rem;
}

.feature-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  padding: 2rem;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.feature-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.feature-card.premium {
  border-color: #f59e0b;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.feature-card.premium:hover {
  border-color: #d97706;
  box-shadow: 0 8px 25px rgba(245, 158, 11, 0.25);
}

.feature-icon {
  width: 64px;
  height: 64px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.feature-icon.core {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #1e40af;
}

.feature-icon.premium {
  background: linear-gradient(135deg, #fef3c7 0%, #fde68a 100%);
  color: #92400e;
}

.feature-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.feature-name {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.feature-description {
  color: #64748b;
  font-size: 0.95rem;
  line-height: 1.5;
  margin-bottom: 1.5rem;
}

.feature-benefits {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.benefit-tag {
  background: #f1f5f9;
  color: #475569;
  font-size: 0.75rem;
  font-weight: 500;
  padding: 0.35rem 0.75rem;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.feature-card.premium .benefit-tag {
  background: #fef3c7;
  color: #92400e;
  border-color: #fbbf24;
}

/* Custom Toggle Switch */
.feature-toggle {
  position: relative;
}

.toggle-input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-label {
  position: relative;
  display: inline-block;
  width: 44px;
  height: 24px;
  background: #cbd5e1;
  border-radius: 24px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.toggle-label:before {
  content: "";
  position: absolute;
  top: 2px;
  left: 2px;
  width: 20px;
  height: 20px;
  background: white;
  border-radius: 50%;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

.toggle-input:checked + .toggle-label {
  background: var(--bs-primary);
}

.toggle-input:checked + .toggle-label.premium {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
}

.toggle-input:checked + .toggle-label:before {
  transform: translateX(20px);
}

.toggle-label:hover {
  box-shadow: 0 0 0 6px rgba(59, 130, 246, 0.1);
}

.toggle-label.premium:hover {
  box-shadow: 0 0 0 6px rgba(245, 158, 11, 0.1);
}

.toggle-input:focus + .toggle-label {
  outline: 2px solid var(--bs-primary);
  outline-offset: 2px;
}

.company-form-grid {
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.form-section {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 2rem;
  transition: all 0.3s ease;
}

.form-section:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.form-fields {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  margin-top: 1.5rem;
}

.form-field {
  display: flex;
  flex-direction: column;
}

.form-field.full-width {
  grid-column: 1 / -1;
}

.form-field.half-width {
  grid-column: span 1;
}

.modern-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.modern-input {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.875rem;
  transition: all 0.3s ease;
  background: white;
}

.modern-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.modern-input::placeholder {
  color: #9ca3af;
}

.modern-input:hover {
  border-color: #cbd5e1;
}

.modern-input.error {
  border-color: #ef4444;
}

.modern-input.error:focus {
  border-color: #ef4444;
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.field-error {
  color: #ef4444;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.field-hint {
  color: #6b7280;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.modern-input.success {
  border-color: #10b981;
}

.modern-input.success:focus {
  border-color: #10b981;
  box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.1);
}

.form-section-wrapper {
  margin-bottom: 3rem;
}

.section-header-external {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.section-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border: 1px solid #93c5fd;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #1e40af;
  flex-shrink: 0;
}

.section-heading-external {
  font-size: 1.25rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
}

.section-description {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0.25rem 0 0 0;
}

.modern-button {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  font-size: 0.875rem;
  border: none;
  transition: all 0.3s ease;
  cursor: pointer;
  text-decoration: none;
}

.modern-button.primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.modern-button.primary:hover {
  background: linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

@media (max-width: 768px) {
  .section-header-external {
    gap: 0.75rem;
  }
  
  .section-icon {
    width: 40px;
    height: 40px;
  }
  
  .section-heading-external {
    font-size: 1.125rem;
  }
  
  .form-section {
    padding: 1.5rem;
  }
  
  .form-fields {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
  .form-field.half-width {
    grid-column: 1;
  }
  
  .user-grid {
    grid-template-columns: 1fr;
  }
  
  .invitation-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .invitation-actions {
    align-self: flex-end;
  }
  
  .feature-cards {
    grid-template-columns: 1fr;
  }
  
  .feature-card {
    padding: 1.5rem;
  }
  
  .category-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}

@media (max-width: 640px) {
  .form-section {
    padding: 1rem;
  }
  
  .section-icon {
    width: 36px;
    height: 36px;
  }
  
  .modern-card-header {
    padding: 1rem 1.5rem;
  }
  
  .modern-card-body {
    padding: 1.5rem;
  }
}
/* Payment Methods Grid */
.payment-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.payment-method-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s ease;
}

.payment-method-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.payment-method-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.payment-method-info {
  flex: 1;
}

.payment-method-name {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.payment-method-description {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.4;
}

.payment-method-config {
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

/* Delivery Methods Grid */
.delivery-methods-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.delivery-method-card {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  transition: all 0.2s ease;
}

.delivery-method-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.delivery-method-card.premium {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
}

.delivery-method-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1rem;
}

.delivery-method-info {
  flex: 1;
}

.delivery-method-name {
  font-size: 1rem;
  font-weight: 600;
  color: #374151;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.delivery-method-description {
  color: #6b7280;
  font-size: 0.875rem;
  margin: 0;
  line-height: 1.4;
}

.delivery-method-config {
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.delivery-method-card.premium .delivery-method-config {
  border-top-color: #fbbf24;
}

/* Default Method Section */
.default-method-section {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.5rem;
  margin-top: 2rem;
}

/* Responsive Design for Payment/Delivery Methods */
@media (max-width: 768px) {
  .payment-methods-grid,
  .delivery-methods-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }
  
.payment-method-card, .delivery-method-card {
    padding: 1.25rem;
}
        
.payment-method-header,
  .delivery-method-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
}

  .nav-tabs {
    border-bottom: 2px solid #e2e8f0;
    background: #f8fafc;
    border-radius: 12px 12px 0 0;
    padding: 0.5rem 1rem 0 1rem;
    margin-bottom: 0;
  }

  .nav-tabs .nav-link {
    border: none;
    border-radius: 8px 8px 0 0;
    color: #64748b;
    font-weight: 500;
    font-size: 0.875rem;
    padding: 1rem 1.5rem;
    margin-right: 0.25rem;
    transition: all 0.3s ease;
    background: transparent;
    position: relative;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .nav-tabs .nav-link:hover {
    border-color: transparent;
    background: rgba(59, 130, 246, 0.1);
    color: #3b82f6;
    transform: translateY(-1px);
  }

  .nav-tabs .nav-link.active {
    background: white;
    color: #1e293b;
    border-color: transparent;
    box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.1);
    position: relative;
  }

  .nav-tabs .nav-link.active::after {
    content: '';
    position: absolute;
    bottom: -2px;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--bs-primary);
    border-radius: 3px 3px 0 0;
  }

  .nav-tabs .nav-link svg {
    transition: all 0.3s ease;
  }

  .nav-tabs .nav-link:hover svg {
    transform: scale(1.1);
  }

  .nav-tabs .nav-link.active svg {
    color: #3b82f6;
  }

  /* Tab Content Enhancement */
  .tab-content {
    //background: white;
    border-radius: 0 0 12px 12px;
    min-height: 400px;
  }

  /*.tab-pane {
    padding: 2rem;
  }*/

  /* Responsive Tab Design */
  @media (max-width: 768px) {
    .nav-tabs {
      padding: 0.25rem;
      overflow-x: auto;
      white-space: nowrap;
      -webkit-overflow-scrolling: touch;
    }
        
    .nav-tabs .nav-link {
      padding: 0.75rem 1rem;
      font-size: 0.8125rem;
      flex-shrink: 0;
    }
        
    .tab-pane {
      padding: 1rem;
    }
  }

  @media (max-width: 640px) {
    .nav-tabs .nav-link span:last-child {
      display: none;
    }
        
    .nav-tabs .nav-link {
      padding: 0.75rem;
      justify-content: center;
    }
  }

  /* Tab Icon Animations */
  @keyframes tabIconPulse {
    0% { transform: scale(1); }
    50% { transform: scale(1.05); }
    100% { transform: scale(1); }
  }

  .nav-tabs .nav-link.active svg {
    animation: tabIconPulse 0.6s ease-in-out;
  }

  /* Enhanced Tab Container */
  .settings-tabs-container {
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    margin-bottom: 2rem;
  }

  .tab-title {
  position: relative;
  padding: 0.25rem 0;
}

.tab-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: rgba(148, 163, 184, 0.1);
  transition: all 0.3s ease;
}

.nav-tabs .nav-link:hover .tab-icon-wrapper {
  background: rgba(59, 130, 246, 0.15);
}

.nav-tabs .nav-link.active .tab-icon-wrapper {
  background: rgba(59, 130, 246, 0.1);
}

.tab-text {
  font-weight: 500;
  letter-spacing: 0.025em;
}

.premium-indicator {
  position: relative;
  margin-left: 0.25rem;
}

.premium-dot {
  width: 6px;
  height: 6px;
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  border-radius: 50%;
  display: block;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0% { opacity: 1; }
  50% { opacity: 0.5; }
  100% { opacity: 1; }
}

/* Enhanced Tab Content Transitions */
.tab-pane {
  opacity: 0;
  transform: translateY(10px);
  transition: all 0.3s ease;
}

.tab-pane.active {
  opacity: 1;
  transform: translateY(0);
}

/* Mobile-first responsive enhancements */
@media (max-width: 576px) {
  .tab-text {
    font-size: 0.75rem;
  }
  
  .tab-icon-wrapper {
    width: 20px;
    height: 20px;
  }
  
  .tab-icon-wrapper svg {
    width: 14px;
    height: 14px;
  }
}
`}</style>

      <PageTitle activeMenu="Företaget" motherMenu="Inställningar" />

      <Tabs
        defaultActiveKey="invoice"
        id="settings-tabs"
        className="mb-4 enhanced-tabs"
      >
        {/* Invoice Settings Tab */}

        <Tab
          eventKey="invoice"
          title={
            <span className="d-flex align-items-center tab-title">
              <div className="tab-icon-wrapper">
                <FileText className="me-2" size={18} />
              </div>
              <span className="tab-text">Fakturor</span>
              <div className="tab-indicator"></div>
            </span>
          }
        >
          {/* Invoice Numbering Section */}
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <FileText size={24} />
                  Fakturanumrering
                </h2>
                <p className="section-subtitle">
                  Konfigurera hur dina fakturanummer genereras
                </p>
              </div>
              <div className="action-buttons">
                <Button
                  variant="primary"
                  className="ms-2"
                  onClick={saveInvoiceNumbering}
                >
                  <Save className="me-2" size={16} />
                  Spara fakturanumrering
                </Button>
              </div>
            </div>

            <div className="modern-card-body">
              <div className="company-form-grid">
                <div className="form-section-wrapper">
                  <div className="section-header-external">
                    <div className="section-icon">
                      <FileText size={20} />
                    </div>
                    <div>
                      <h3 className="section-heading-external">Numreringsinställningar</h3>
                      <p className="section-description">Konfigurera prefix, startnummer och format för fakturor</p>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-fields">
                      <div className="form-field half-width">
                        <label htmlFor="invoicePrefix" className="modern-label">
                          Prefix
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="invoicePrefix"
                          placeholder="INV-"
                          value={invoiceSettings.invoiceNumbering.prefix}
                          onChange={(e) => updateInvoiceSetting('invoiceNumbering', { prefix: e.target.value })}
                        />
                      </div>
                      <div className="form-field half-width">
                        <label htmlFor="invoiceStartNumber" className="modern-label">
                          Startnummer
                        </label>
                        <input
                          type="number"
                          className="modern-input"
                          id="invoiceStartNumber"
                          placeholder="1001"
                          value={invoiceSettings.invoiceNumbering.startNumber}
                          onChange={(e) => updateInvoiceSetting('invoiceNumbering', {
                            startNumber: parseInt(e.target.value, 10) || 1001
                          })}
                        />
                      </div>
                      <div className="form-field half-width">
                        <label htmlFor="invoicePadding" className="modern-label">
                          Antal siffror
                        </label>
                        <input
                          type="number"
                          className="modern-input"
                          id="invoicePadding"
                          min="1"
                          max="10"
                          value={invoiceSettings.invoiceNumbering.padding}
                          onChange={(e) => updateInvoiceSetting('invoiceNumbering', {
                            padding: parseInt(e.target.value, 10) || 4
                          })}
                        />
                      </div>
                      <div className="form-field half-width">
                        <div className="form-check" style={{ marginTop: "2rem" }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="invoiceIncludeYear"
                            checked={invoiceSettings.invoiceNumbering.includeYear}
                            onChange={(e) => updateInvoiceSetting('invoiceNumbering', { includeYear: e.target.checked })}
                          />
                          <label className="form-check-label modern-label" htmlFor="invoiceIncludeYear">
                            Inkludera år i fakturanummer
                          </label>
                        </div>
                      </div>
                      <div className="form-field half-width">
                        <div className="form-check" style={{ marginTop: "2rem" }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="invoiceResetYearly"
                            checked={invoiceSettings.invoiceNumbering.resetYearly}
                            onChange={(e) => updateInvoiceSetting('invoiceNumbering', { resetYearly: e.target.checked })}
                          />
                          <label className="form-check-label modern-label" htmlFor="invoiceResetYearly">
                            Återställ startnummer varje år
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Preview for Invoice */}
                    <div className="form-field full-width" style={{ marginTop: "1rem", padding: "1rem", background: "#f8f9fa", borderRadius: "8px" }}>
                      <label className="modern-label">Förhandsgranskning:</label>
                      <div style={{ fontFamily: "monospace", fontSize: "1.1rem", color: "#495057" }}>
                        {invoiceSettings.invoiceNumbering.includeYear
                          ? `${invoiceSettings.invoiceNumbering.prefix}${new Date().getFullYear()}-${String(invoiceSettings.invoiceNumbering.startNumber).padStart(invoiceSettings.invoiceNumbering.padding, '0')}`
                          : `${invoiceSettings.invoiceNumbering.prefix}${String(invoiceSettings.invoiceNumbering.startNumber).padStart(invoiceSettings.invoiceNumbering.padding, '0')}`
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Terms Section */}
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12,6 12,12 16,14" />
                  </svg>
                  Betalningsvillkor
                </h2>
                <p className="section-subtitle">
                  Konfigurera standardvillkor för dina fakturor
                </p>
              </div>
              <div className="action-buttons">
                <Button
                  variant="primary"
                  className="ms-2"
                  onClick={() => savePaymentTerms()}
                >
                  <Save className="me-2" size={16} />
                  Spara betalningsvillkor
                </Button>
              </div>
            </div>

            <div className="modern-card-body">
              <div className="company-form-grid">
                <div className="form-section-wrapper">
                  <div className="section-header-external">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12,6 12,12 16,14" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="section-heading-external">Betalningsperioder</h3>
                      <p className="section-description">Ange standardbetalningsvillkor och rabatter</p>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-fields">
                      <div className="form-field half-width">
                        <label htmlFor="paymentDays" className="modern-label">
                          Standardbetalningsvillkor (dagar)
                        </label>
                        <input
                          type="number"
                          className="modern-input"
                          id="paymentDays"
                          placeholder="30"
                          value={invoiceSettings.defaultTerms.paymentDays}
                          onChange={(e) => updateInvoiceSetting('defaultTerms', {
                            paymentDays: parseInt(e.target.value, 10) || 30
                          })}
                        />
                      </div>
                      <div className="form-field half-width">
                        <label htmlFor="lateFeePercentage" className="modern-label">
                          Dröjsmålsränta (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          className="modern-input"
                          id="lateFeePercentage"
                          placeholder="8.5"
                          value={invoiceSettings.defaultTerms.lateFeePercentage}
                          onChange={(e) => updateInvoiceSetting('defaultTerms', {
                            lateFeePercentage: parseFloat(e.target.value) || 0
                          })}
                        />
                      </div>
                      <div className="form-field half-width">
                        <label htmlFor="discountDays" className="modern-label">
                          Rabattperiod (dagar)
                        </label>
                        <input
                          type="number"
                          className="modern-input"
                          id="discountDays"
                          placeholder="10"
                          value={invoiceSettings.defaultTerms.discountDays}
                          onChange={(e) => updateInvoiceSetting('defaultTerms', {
                            discountDays: parseInt(e.target.value, 10) || 0
                          })}
                        />
                      </div>
                      <div className="form-field half-width">
                        <label htmlFor="discountPercentage" className="modern-label">
                          Rabatt för förskottsbetalning (%)
                        </label>
                        <input
                          type="number"
                          step="0.1"
                          className="modern-input"
                          id="discountPercentage"
                          placeholder="2.0"
                          value={invoiceSettings.defaultTerms.discountPercentage}
                          onChange={(e) => updateInvoiceSetting('defaultTerms', {
                            discountPercentage: parseFloat(e.target.value) || 0
                          })}
                        />
                      </div>
                      <div className="form-field full-width">
                        <div className="form-check" style={{ marginTop: "1rem" }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="approvedForFSkatt"
                            checked={invoiceSettings.defaultTerms.fskattApproved}
                            onChange={(e) => updateInvoiceSetting('defaultTerms', { fskattApproved: e.target.checked })}
                          />
                          <label className="form-check-label modern-label" htmlFor="approvedForFSkatt" style={{ marginBottom: 0 }}>
                            Godkänd för F-skatt
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment Methods Section */}
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
                    <line x1="1" y1="10" x2="23" y2="10" />
                  </svg>
                  Betalningsmetoder
                </h2>
                <p className="section-subtitle">
                  Konfigurera tillgängliga betalningsmetoder för dina fakturor
                </p>
              </div>
              <div className="action-buttons">
                <Button
                  variant="primary"
                  className="ms-2"
                  onClick={() => savePaymentMethods()}
                >
                  <Save className="me-2" size={16} />
                  Spara betalningsmetoder
                </Button>
              </div>
            </div>

            <div className="modern-card-body">
              <div className="payment-methods-grid">
                {/* Bankgiro */}
                <div className="payment-method-card">
                  <div className="payment-method-header">
                    <div className="payment-method-info">
                      <h4 className="payment-method-name">Bankgiro</h4>
                      <p className="payment-method-description">Tillåt kunder att betala via bankgiro</p>
                    </div>
                    <div className="feature-toggle">
                      <input
                        className="toggle-input"
                        type="checkbox"
                        id="bankgiroSwitch"
                        checked={invoiceSettings.paymentMethods.bankgiro}
                        onChange={() => togglePaymentMethod('bankgiro')}
                      />
                      <label htmlFor="bankgiroSwitch" className="toggle-label"></label>
                    </div>
                  </div>
                  {invoiceSettings.paymentMethods.bankgiro && (
                    <div className="payment-method-config">
                      <div className="form-field">
                        <label htmlFor="bankgiroNumber" className="modern-label">
                          Bankgironummer
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="bankgiroNumber"
                          placeholder="XXXX-XXXX"
                          value={invoiceSettings.paymentMethods.bankgiroNumber}
                          onChange={(e) => updateInvoiceSetting('paymentMethods', {
                            bankgiroNumber: e.target.value
                          })}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Plusgiro */}
                <div className="payment-method-card">
                  <div className="payment-method-header">
                    <div className="payment-method-info">
                      <h4 className="payment-method-name">Plusgiro</h4>
                      <p className="payment-method-description">Tillåt kunder att betala via plusgiro</p>
                    </div>
                    <div className="feature-toggle">
                      <input
                        className="toggle-input"
                        type="checkbox"
                        id="plusgiroSwitch"
                        checked={invoiceSettings.paymentMethods.plusgiro}
                        onChange={() => togglePaymentMethod('plusgiro')}
                      />
                      <label htmlFor="plusgiroSwitch" className="toggle-label"></label>
                    </div>
                  </div>
                  {invoiceSettings.paymentMethods.plusgiro && (
                    <div className="payment-method-config">
                      <div className="form-field">
                        <label htmlFor="plusgiroNumber" className="modern-label">
                          Plusgironummer
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="plusgiroNumber"
                          placeholder="XXXXXX-X"
                          value={invoiceSettings.paymentMethods.plusgiroNumber}
                          onChange={(e) => updateInvoiceSetting('paymentMethods', {
                            plusgiroNumber: e.target.value
                          })}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* PayPal */}
                <div className="payment-method-card">
                  <div className="payment-method-header">
                    <div className="payment-method-info">
                      <h4 className="payment-method-name">PayPal</h4>
                      <p className="payment-method-description">Tillåt kunder att betala med PayPal</p>
                    </div>
                    <div className="feature-toggle">
                      <input
                        className="toggle-input"
                        type="checkbox"
                        id="paypalSwitch"
                        checked={invoiceSettings.paymentMethods.paypal}
                        onChange={() => togglePaymentMethod('paypal')}
                      />
                      <label htmlFor="paypalSwitch" className="toggle-label"></label>
                    </div>
                  </div>
                  {invoiceSettings.paymentMethods.paypal && (
                    <div className="payment-method-config">
                      <div className="form-field">
                        <label htmlFor="paypalEmail" className="modern-label">
                          PayPal E-post
                        </label>
                        <input
                          type="email"
                          className="modern-input"
                          id="paypalEmail"
                          placeholder="exempel@foretag.se"
                          value={invoiceSettings.paymentMethods.paypalEmail}
                          onChange={(e) => updateInvoiceSetting('paymentMethods', {
                            paypalEmail: e.target.value
                          })}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Swish */}
                <div className="payment-method-card">
                  <div className="payment-method-header">
                    <div className="payment-method-info">
                      <h4 className="payment-method-name">Swish</h4>
                      <p className="payment-method-description">Tillåt kunder att betala med Swish</p>
                    </div>
                    <div className="feature-toggle">
                      <input
                        className="toggle-input"
                        type="checkbox"
                        id="swishSwitch"
                        checked={invoiceSettings.paymentMethods.swish}
                        onChange={() => togglePaymentMethod('swish')}
                      />
                      <label htmlFor="swishSwitch" className="toggle-label"></label>
                    </div>
                  </div>
                  {invoiceSettings.paymentMethods.swish && (
                    <div className="payment-method-config">
                      <div className="form-field">
                        <label htmlFor="swishNumber" className="modern-label">
                          Swish-nummer
                        </label>
                        <input
                          type="tel"
                          className="modern-input"
                          id="swishNumber"
                          placeholder="123 456 78 90"
                          value={invoiceSettings.paymentMethods.swishNumber}
                          onChange={(e) => updateInvoiceSetting('paymentMethods', {
                            swishNumber: e.target.value
                          })}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="default-method-section">
                <div className="form-field">
                  <label htmlFor="defaultPaymentMethod" className="modern-label">
                    Förvald betalningsmetod
                  </label>
                  <select
                    className="modern-input"
                    id="defaultPaymentMethod"
                    value={invoiceSettings.paymentMethods.defaultMethod}
                    onChange={(e) => updateInvoiceSetting('paymentMethods', {
                      defaultMethod: e.target.value
                    })}
                  >
                    {invoiceSettings.paymentMethods.bankgiro && (
                      <option value="bankgiro">Bankgiro</option>
                    )}
                    {invoiceSettings.paymentMethods.plusgiro && (
                      <option value="plusgiro">Plusgiro</option>
                    )}
                    {invoiceSettings.paymentMethods.paypal && (
                      <option value="paypal">PayPal</option>
                    )}
                    {invoiceSettings.paymentMethods.swish && (
                      <option value="swish">Swish</option>
                    )}
                  </select>
                  <div className="field-hint">
                    Välj vilken betalningsmetod som ska vara förvald när du skapar nya fakturor
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Methods Section */}
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 3h5v5M4 20L21 3M21 16v5h-5M4 4l5 5" />
                  </svg>
                  Leveransmetoder
                </h2>
                <p className="section-subtitle">
                  Konfigurera hur fakturor levereras till kunder
                </p>
              </div>
              <div className="action-buttons">
                <Button
                  variant="primary"
                  className="ms-2"
                  onClick={() => saveDeliveryMethods()}
                >
                  <Save className="me-2" size={16} />
                  Spara leveransmetoder
                </Button>
              </div>
            </div>

            <div className="modern-card-body">
              <div className="delivery-methods-grid">
                {/* Email Delivery */}
                <div className="delivery-method-card">
                  <div className="delivery-method-header">
                    <div className="delivery-method-info">
                      <h4 className="delivery-method-name">E-post</h4>
                      <p className="delivery-method-description">Skicka fakturor via e-post</p>
                    </div>
                    <div className="feature-toggle">
                      <input
                        className="toggle-input"
                        type="checkbox"
                        id="emailSwitch"
                        checked={invoiceSettings.deliveryChannels.email}
                        onChange={() => toggleDeliveryMethod('email')}
                      />
                      <label htmlFor="emailSwitch" className="toggle-label"></label>
                    </div>
                  </div>
                  {invoiceSettings.deliveryChannels.email && (
                    <div className="delivery-method-config">
                      <div className="form-field">
                        <label htmlFor="emailFrom" className="modern-label">
                          Avsändaradress *
                        </label>
                        <input
                          type="email"
                          className={`modern-input ${formErrors.emailFrom ? 'error' : ''}`}
                          id="emailFrom"
                          placeholder="faktura@dittföretag.se"
                          value={invoiceSettings.deliveryChannels.emailFrom || ""}
                          onChange={(e) => {
                            updateInvoiceSetting('deliveryChannels', {
                              emailFrom: e.target.value
                            });
                            if (formErrors.emailFrom) {
                              setFormErrors({ ...formErrors, emailFrom: null });
                            }
                          }}
                        />
                        {formErrors.emailFrom && (
                          <div className="field-error">
                            {formErrors.emailFrom}
                          </div>
                        )}
                        <div className="field-hint">
                          E-postadressen som fakturor skickas från
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Download Delivery */}
                <div className="delivery-method-card">
                  <div className="delivery-method-header">
                    <div className="delivery-method-info">
                      <h4 className="delivery-method-name">Nedladdning</h4>
                      <p className="delivery-method-description">Generera utskrivbara PDF-fakturor</p>
                    </div>
                    <div className="feature-toggle">
                      <input
                        className="toggle-input"
                        type="checkbox"
                        id="downloadSwitch"
                        checked={invoiceSettings.deliveryChannels.download}
                        onChange={() => toggleDeliveryMethod('download')}
                      />
                      <label htmlFor="downloadSwitch" className="toggle-label"></label>
                    </div>
                  </div>
                  {invoiceSettings.deliveryChannels.download && (
                    <div className="delivery-method-config">
                      <div className="form-field">
                        <label htmlFor="printFormat" className="modern-label">
                          Pappersformat
                        </label>
                        <select
                          className="modern-input"
                          id="printFormat"
                          value={invoiceSettings.deliveryChannels.printFormat || "A4"}
                          onChange={(e) => updateInvoiceSetting('deliveryChannels', {
                            printFormat: e.target.value
                          })}
                        >
                          <option value="A4">A4</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* E-Invoice */}
                <div className="delivery-method-card premium">
                  <div className="delivery-method-header">
                    <div className="delivery-method-info">
                      <h4 className="delivery-method-name">
                        E-faktura
                        <span className="badge bg-warning text-dark ms-2">Premium</span>
                      </h4>
                      <p className="delivery-method-description">Skicka fakturor som elektroniska fakturor</p>
                    </div>
                    <div className="feature-toggle">
                      <input
                        className="toggle-input"
                        type="checkbox"
                        id="eInvoiceSwitch"
                        disabled={true}
                        checked={invoiceSettings.deliveryChannels.eInvoice}
                        onChange={() => toggleDeliveryMethod('eInvoice')}
                      />
                      <label htmlFor="eInvoiceSwitch" className="toggle-label premium"></label>
                    </div>
                  </div>
                  {invoiceSettings.deliveryChannels.eInvoice && (
                    <div className="delivery-method-config">
                      <div className="form-field">
                        <label htmlFor="eInvoiceProvider" className="modern-label">
                          E-faktura leverantör
                        </label>
                        <select
                          className="modern-input"
                          id="eInvoiceProvider"
                          value={invoiceSettings.deliveryChannels.eInvoiceProvider || ""}
                          onChange={(e) => updateInvoiceSetting('deliveryChannels', {
                            eInvoiceProvider: e.target.value
                          })}
                        >
                          <option value="">Välj leverantör</option>
                          <option value="svefaktura">Svefaktura</option>
                          <option value="peppol">PEPPOL</option>
                          <option value="finvoice">Finvoice</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>

                {/* SMS Delivery */}
                <div className="delivery-method-card premium">
                  <div className="delivery-method-header">
                    <div className="delivery-method-info">
                      <h4 className="delivery-method-name">
                        SMS
                        <span className="badge bg-warning text-dark ms-2">Premium</span>
                      </h4>
                      <p className="delivery-method-description">Skicka faktura notifieringar via SMS</p>
                    </div>
                    <div className="feature-toggle">
                      <input
                        className="toggle-input"
                        type="checkbox"
                        id="smsSwitch"
                        disabled={true}
                        checked={invoiceSettings.deliveryChannels.sms}
                        onChange={() => toggleDeliveryMethod('sms')}
                      />
                      <label htmlFor="smsSwitch" className="toggle-label premium"></label>
                    </div>
                  </div>
                  {invoiceSettings.deliveryChannels.sms && (
                    <div className="delivery-method-config">
                      <div className="form-field">
                        <label htmlFor="smsFrom" className="modern-label">
                          Avsändarnummer
                        </label>
                        <input
                          type="text"
                          className={`modern-input ${formErrors.smsFrom ? 'error' : ''}`}
                          id="smsFrom"
                          placeholder="Ex: Företagsnamn"
                          value={invoiceSettings.deliveryChannels.smsFrom || ""}
                          onChange={(e) => {
                            updateInvoiceSetting('deliveryChannels', {
                              smsFrom: e.target.value
                            });
                            if (formErrors.smsFrom) {
                              setFormErrors({ ...formErrors, smsFrom: null });
                            }
                          }}
                        />
                        {formErrors.smsFrom && (
                          <div className="field-error">
                            {formErrors.smsFrom}
                          </div>
                        )}
                        <div className="field-hint">
                          Avsändarnamn eller telefonnummer
                        </div>
                      </div>
                      <div className="form-field">
                        <label htmlFor="smsTemplate" className="modern-label">
                          SMS mall
                        </label>
                        <input
                          type="text"
                          className={`modern-input ${formErrors.smsTemplate ? 'error' : ''}`}
                          id="smsTemplate"
                          placeholder="Faktura {invoiceNumber} har skapats. Se din e-post för detaljer."
                          value={invoiceSettings.deliveryChannels.smsTemplate || ""}
                          onChange={(e) => {
                            updateInvoiceSetting('deliveryChannels', {
                              smsTemplate: e.target.value
                            });
                            if (formErrors.smsTemplate) {
                              setFormErrors({ ...formErrors, smsTemplate: null });
                            }
                          }}
                        />
                        {formErrors.smsTemplate && (
                          <div className="field-error">
                            {formErrors.smsTemplate}
                          </div>
                        )}
                        <div className="field-hint">
                          Använd {'{invoiceNumber}'}, {'{dueDate}'}, {'{total}'} som ersätts med faktiska värden
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div className="default-method-section">
                <div className="form-field">
                  <label htmlFor="defaultDeliveryChannel" className="modern-label">
                    Förvald leveransmetod
                  </label>
                  <select
                    className="modern-input"
                    id="defaultDeliveryChannel"
                    value={invoiceSettings.deliveryChannels.defaultChannel}
                    onChange={(e) => updateInvoiceSetting('deliveryChannels', {
                      defaultChannel: e.target.value
                    })}
                  >
                    {invoiceSettings.deliveryChannels.email && (
                      <option value="email">E-post</option>
                    )}
                    {invoiceSettings.deliveryChannels.download && (
                      <option value="download">Ladda ner PDF</option>
                    )}
                    {invoiceSettings.deliveryChannels.eInvoice && (
                      <option value="eInvoice">E-faktura</option>
                    )}
                    {invoiceSettings.deliveryChannels.sms && (
                      <option value="sms">SMS</option>
                    )}
                  </select>
                  <div className="field-hint">
                    Välj vilken leveransmetod som ska vara förvald när du skapar nya fakturor
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Tab>

        {/* Quote Settings Tab */}
        <Tab
          eventKey="quotes"
          title={
            <span className="d-flex align-items-center tab-title">
              <div className="tab-icon-wrapper">
                <FileText className="me-2" size={18} />
              </div>
              <span className="tab-text">Offerter</span>
              <div className="tab-indicator"></div>
            </span>
          }
        >
          {/* Quote Numbering Section */}
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <FileText size={24} />
                  Offertnumrering
                </h2>
                <p className="section-subtitle">
                  Konfigurera hur dina offertnummer genereras
                </p>
              </div>
              <div className="action-buttons">
                <Button
                  variant="primary"
                  className="ms-2"
                  onClick={saveQuoteNumbering}
                >
                  <Save className="me-2" size={16} />
                  Spara offertnumrering
                </Button>
              </div>
            </div>

            <div className="modern-card-body">
              <div className="company-form-grid">
                <div className="form-section-wrapper">
                  <div className="section-header-external">
                    <div className="section-icon">
                      <FileText size={20} />
                    </div>
                    <div>
                      <h3 className="section-heading-external">Numreringsinställningar</h3>
                      <p className="section-description">Konfigurera prefix, startnummer och format för offerter</p>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-fields">
                      <div className="form-field half-width">
                        <label htmlFor="quotePrefix" className="modern-label">
                          Prefix
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="quotePrefix"
                          placeholder="Q"
                          value={quoteSettings.quoteNumbering.prefix}
                          onChange={(e) => updateQuoteSetting('quoteNumbering', { prefix: e.target.value })}
                        />
                      </div>
                      <div className="form-field half-width">
                        <label htmlFor="quoteStartNumber" className="modern-label">
                          Startnummer
                        </label>
                        <input
                          type="number"
                          className="modern-input"
                          id="quoteStartNumber"
                          placeholder="1"
                          value={quoteSettings.quoteNumbering.startNumber}
                          onChange={(e) => updateQuoteSetting('quoteNumbering', {
                            startNumber: parseInt(e.target.value, 10) || 1
                          })}
                        />
                      </div>
                      <div className="form-field half-width">
                        <label htmlFor="quotePadding" className="modern-label">
                          Antal siffror
                        </label>
                        <input
                          type="number"
                          className="modern-input"
                          id="quotePadding"
                          min="1"
                          max="10"
                          value={quoteSettings.quoteNumbering.padding}
                          onChange={(e) => updateQuoteSetting('quoteNumbering', {
                            padding: parseInt(e.target.value, 10) || 4
                          })}
                        />
                      </div>
                      <div className="form-field half-width">
                        <div className="form-check" style={{ marginTop: "2rem" }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="quoteIncludeYear"
                            checked={quoteSettings.quoteNumbering.includeYear}
                            onChange={(e) => updateQuoteSetting('quoteNumbering', { includeYear: e.target.checked })}
                          />
                          <label className="form-check-label modern-label" htmlFor="quoteIncludeYear">
                            Inkludera år i offertnummer
                          </label>
                        </div>
                      </div>
                      <div className="form-field half-width">
                        <div className="form-check" style={{ marginTop: "2rem" }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="quoteResetYearly"
                            checked={quoteSettings.quoteNumbering.resetYearly}
                            onChange={(e) => updateQuoteSetting('quoteNumbering', { resetYearly: e.target.checked })}
                          />
                          <label className="form-check-label modern-label" htmlFor="quoteResetYearly">
                            Återställ startnummer varje år
                          </label>
                        </div>
                      </div>
                    </div>

                    {/* Preview for Quote */}
                    <div className="form-field full-width" style={{ marginTop: "1rem", padding: "1rem", background: "#f8f9fa", borderRadius: "8px" }}>
                      <label className="modern-label">Exempel på offertnummer:</label>
                      <div style={{ fontFamily: "monospace", fontSize: "1.1rem", color: "#495057" }}>
                        {quoteSettings.quoteNumbering.includeYear
                          ? `${quoteSettings.quoteNumbering.prefix}${new Date().getFullYear()}-${String(quoteSettings.quoteNumbering.startNumber).padStart(quoteSettings.quoteNumbering.padding, '0')}`
                          : `${quoteSettings.quoteNumbering.prefix}${String(quoteSettings.quoteNumbering.startNumber).padStart(quoteSettings.quoteNumbering.padding, '0')}`
                        }
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quote-specific settings can be added here */}
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <polyline points="12,6 12,12 16,14" />
                  </svg>
                  Offertinställningar
                </h2>
                <p className="section-subtitle">
                  Konfigurera standardinställningar för dina offerter
                </p>
              </div>
              <div className="action-buttons">
                <Button
                  variant="primary"
                  className="ms-2"
                  onClick={() => saveQuoteSettings()}
                >
                  <Save className="me-2" size={16} />
                  Spara offertinställningar
                </Button>
              </div>
            </div>

            <div className="modern-card-body">
              <div className="company-form-grid">
                <div className="form-section-wrapper">
                  <div className="section-header-external">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <polyline points="12,6 12,12 16,14" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="section-heading-external">Giltighetstid</h3>
                      <p className="section-description">Ange hur länge dina offerter ska vara giltiga</p>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-fields">
                      <div className="form-field half-width">
                        <label htmlFor="quoteValidityDays" className="modern-label">
                          Giltighetstid (dagar)
                        </label>
                        <input
                          type="number"
                          className="modern-input"
                          id="quoteValidityDays"
                          placeholder="30"
                          value={quoteSettings.defaultTerms?.validityDays || 30}
                          onChange={(e) => updateQuoteSetting('defaultTerms', {
                            validityDays: parseInt(e.target.value, 10) || 30
                          })}
                        />
                        <div className="field-hint">
                          Antal dagar som offerten är giltig från skapandedatum
                        </div>
                      </div>
                      <div className="form-field half-width">
                        <div className="form-check" style={{ marginTop: "2rem" }}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="quoteAutoConvert"
                            checked={quoteSettings.defaultTerms?.autoConvertToInvoice || false}
                            onChange={(e) => updateQuoteSetting('defaultTerms', {
                              autoConvertToInvoice: e.target.checked
                            })}
                          />
                          <label className="form-check-label modern-label" htmlFor="quoteAutoConvert" style={{ marginBottom: 0 }}>
                            Automatisk konvertering till faktura när offert accepteras
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Tab>

        {/* Bookkeeping Settings Tab */}
        <Tab
          eventKey="bookkeeping"
          title={
            <span className="d-flex align-items-center tab-title">
              <div className="tab-icon-wrapper">
                <BookCheck className="me-2" size={18} />
              </div>
              <span className="tab-text">Bokföring</span>
              <div className="tab-indicator"></div>
            </span>
          }
        >
          {/* Fiscal Years Section */}
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <Calendar size={24} />
                  Räkenskapsår
                </h2>
                <p className="section-subtitle">
                  Hantera och konfigurera räkenskapsår för ditt företag
                </p>
              </div>
              <div className="action-buttons">
                <Button
                  variant="primary"
                  onClick={() => setShowCreateFiscalYearModal(true)}
                  className="d-flex align-items-center gap-2"
                >
                  <PlusCircle size={16} />
                  Skapa nytt räkenskapsår
                </Button>
              </div>
            </div>

            <div className="modern-card-body">
              {fiscalYears.length > 0 ? (
                <div className="modern-table-container">
                  <Table responsive hover className="modern-table">
                    <thead>
                      <tr>
                        <th>Räkenskapsår</th>
                        <th>Period</th>
                        <th>Status</th>
                        <th>Redovisningsmetod</th>
                        <th>Momsperiod</th>
                        <th>Åtgärder</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fiscalYears.map((year) => (
                        <tr
                          key={year.fiscal_year_id}
                          onClick={navigateToFiscalYearDetails(year.fiscal_year_id)}
                          className="clickable-row"
                        >
                          <td>
                            <div className="table-cell-content">
                              <div className="fiscal-year-badge">
                                {year.year}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="table-cell-content">
                              <div className="date-range">
                                <small className="text-muted">
                                  {new Date(year.start_date).toLocaleDateString('sv-SE')} -
                                  {new Date(year.end_date).toLocaleDateString('sv-SE')}
                                </small>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="table-cell-content">
                              <Badge
                                variant={year.state === 'open' ? 'success' : 'secondary'}
                                className="status-badge"
                              >
                                {year.state === 'open' ? 'Öppen' : 'Stängd'}
                              </Badge>
                            </div>
                          </td>
                          <td>
                            <div className="table-cell-content">
                              <span className="method-text">
                                {year.accounting_method === 'accrual' ? 'Faktureringsmetoden' : 'Kontantmetoden'}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="table-cell-content">
                              <span className="vat-period-text">
                                {year.vat_period === 'monthly' ? 'Månadsvis' :
                                  year.vat_period === 'quarterly' ? 'Kvartalsvis' : 'Årsvis'}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="table-actions">
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleFiscalYearAction(year.fiscal_year_id, year.state === 'open' ? 'close' : 'open');
                                }}
                                variant={year.state === 'open' ? 'outline-warning' : 'outline-success'}
                                size="sm"
                                className="action-button"
                              >
                                {year.state === 'open' ? 'Stäng år' : 'Öppna år'}
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="empty-state">
                  <Calendar size={48} className="empty-icon" />
                  <h4>Inga räkenskapsår skapade än</h4>
                  <p>Skapa ditt första räkenskapsår för att komma igång med bokföringen. Ett räkenskapsårs kan också skapas genom att importera bokföringen från ett annat system.</p>
                  <Button
                    variant="primary"
                    onClick={() => setShowCreateFiscalYearModal(true)}
                    className="mt-3"
                  >
                    <PlusCircle className="me-2" size={16} />
                    Skapa räkenskapsår
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Import/Export Section */}
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <Upload size={24} />
                  Import & Export
                </h2>
                <p className="section-subtitle">
                  Importera och exportera bokföringsdata från/till andra system
                </p>
              </div>
            </div>

            <div className="modern-card-body">
              <div className="import-export-grid">
                {/* Import Section */}
                <div className="import-export-card import-card">
                  <div className="card-icon import-icon">
                    <Download size={32} />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">Importera data</h3>
                    <p className="card-description">
                      Importera bokföringsdata från andra system via SIE-filer
                    </p>

                    <div className="file-drop-container">
                      <FileDropZone
                        setFiles={setFiles}
                        fileTypes='.se'
                        minHeight='120px'
                        subText='Dra och släpp .se fil här eller klicka för att välja'
                        acceptedFileTypes='.se'
                        multiple='false'
                      />
                    </div>

                    <Button
                      variant="primary"
                      onClick={handleUpload}
                      disabled={isLoading || files.length === 0}
                      className="w-100 import-button"
                    >
                      <Upload className="me-2" size={16} />
                      {isLoading ? 'Kontrollerar...' : 'Kontrollera och importera'}
                      {isLoading && (
                        <span className="spinner-border spinner-border-sm ms-2" />
                      )}
                    </Button>
                  </div>
                </div>

                {/* Export Section */}
                <div className="import-export-card export-card">
                  <div className="card-icon export-icon">
                    <Upload size={32} />
                  </div>
                  <div className="card-content">
                    <h3 className="card-title">Exportera data</h3>
                    <p className="card-description">
                      Exportera bokföringsdata till andra system i olika format
                    </p>

                    <div className="export-options">
                      <div className="export-option">
                        <div className="export-option-info">
                          <div className="export-format">
                            <FileText size={20} />
                            <div>
                              <h6>SIE-format</h6>
                              <small>Standard export format för svenska system</small>
                            </div>
                          </div>
                        </div>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={handleOpenExportModal}
                          disabled={isLoading}
                          className="export-button"
                        >
                          <Download size={14} />
                          Exportera
                        </Button>
                      </div>

                      <div className="export-option disabled">
                        <div className="export-option-info">
                          <div className="export-format">
                            <FileText size={20} />
                            <div>
                              <h6>CSV-format</h6>
                              <small>Comma-separated values för Excel</small>
                            </div>
                          </div>
                        </div>
                        <Button variant="outline-secondary" size="sm" disabled>
                          <span className="badge bg-warning text-dark me-2">Snart</span>
                          Exportera
                        </Button>
                      </div>

                      <div className="export-option disabled">
                        <div className="export-option-info">
                          <div className="export-format">
                            <FileText size={20} />
                            <div>
                              <h6>Excel-format</h6>
                              <small>Excel arbetsbok (.xlsx)</small>
                            </div>
                          </div>
                        </div>
                        <Button variant="outline-secondary" size="sm" disabled>
                          <span className="badge bg-warning text-dark me-2">Snart</span>
                          Exportera
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Templates Section */}
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <BookCopy size={24} />
                  Bokföringsmallar
                </h2>
                <p className="section-subtitle">
                  Skapa och hantera mallar för återkommande bokföringstransaktioner
                </p>
              </div>
              <div className="action-buttons">
                <Button
                  variant="primary"
                  onClick={() => navigateToTemplateEditor()}
                  className="d-flex align-items-center gap-2"
                >
                  <PlusCircle size={16} />
                  Skapa ny mall
                </Button>
              </div>
            </div>

            <div className="modern-card-body">
              {templates.length > 0 ? (
                <div className="templates-grid">
                  {templates.map((template) => (
                    <div key={template.template_id} className="template-card">
                      <div className="template-icon">
                        <BookCopy size={24} />
                      </div>
                      <div className="template-content">
                        <h4 className="template-name">{template.template_name}</h4>
                        <p className="template-description">{template.description}</p>
                      </div>
                      <div className="template-actions">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => navigateToTemplateEditor(template.template_id)}
                          className="template-edit-btn"
                        >
                          <Edit size={14} />
                          Redigera
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <BookCopy size={48} className="empty-icon" />
                  <h4>Inga mallar skapade än</h4>
                  <p>Skapa mallar för att effektivisera återkommande bokföringstransaktioner</p>
                  <Button
                    variant="primary"
                    onClick={() => navigateToTemplateEditor()}
                    className="mt-3"
                  >
                    <Plus className="me-2" size={16} />
                    Skapa första mallen
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Account Plan Section 
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <BookA size={24} />
                  Kontoplan
                </h2>
                <p className="section-subtitle">
                  Översikt över alla konton i din kontoplan
                </p>
              </div>
            </div>

            <div className="modern-card-body">
              {accountPlan.length > 0 ? (
                <div className="modern-table-container">
                  <Table responsive hover className="modern-table account-table">
                    <thead>
                      <tr>
                        <th>Kontonummer</th>
                        <th>Kontonamn</th>
                        <th>Kontotyp</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {accountPlan.map((account) => (
                        <tr key={account.account_id}>
                          <td>
                            <div className="account-number">
                              {account.account_number}
                            </div>
                          </td>
                          <td>
                            <div className="account-name">
                              {account.account_name}
                            </div>
                          </td>
                          <td>
                            <div className="account-type">
                              <span className="account-type-badge">
                                {account.account_type}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="account-status">
                              {!account.is_base_plan && (
                                <Badge variant="info" className="custom-account-badge">
                                  Eget konto
                                </Badge>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="empty-state">
                  <BookA size={48} className="empty-icon" />
                  <h4>Ingen kontoplan laddad</h4>
                  <p>Kontoplanen kunde inte laddas. Kontakta support om problemet kvarstår.</p>
                </div>
              )}
            </div>
          </div>*/}
        </Tab>

        {/* Company Details Tab */}
        <Tab
          eventKey="company"
          title={
            <span className="d-flex align-items-center tab-title">
              <div className="tab-icon-wrapper">
                <Building className="me-2" size={18} />
              </div>
              <span className="tab-text">Företagsuppgifter</span>
              <div className="tab-indicator"></div>
            </span>
          }
        >
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <Building size={24} />
                  Företagsinformation
                </h2>
                <p className="section-subtitle">
                  Uppdatera dina företagsuppgifter som visas på fakturor och offerter
                </p>
              </div>
              <div className="action-buttons">
                <Button
                  variant="primary"
                  className="d-flex align-items-center gap-2"
                  onClick={saveCompanyDetails}
                >
                  <Save className="me-2" size={16} />
                  Spara företagsuppgifter
                </Button>
                {/*<button
                  type="button"
                  className="modern-button primary"
                  onClick={saveCompanyDetails}
                >
                  <Save size={16} />
                  Spara företagsuppgifter
                </button>*/}
              </div>
            </div>

            <div className="modern-card-body">
              <div className="company-form-grid">
                {/* Basic Information Section */}
                <div className="form-section-wrapper">
                  <div className="section-header-external">
                    <div className="section-icon">
                      <Building size={20} />
                    </div>
                    <div>
                      <h3 className="section-heading-external">Grunduppgifter</h3>
                      <p className="section-description">Företagets grundläggande information</p>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-fields">
                      <div className="form-field full-width">
                        <label htmlFor="companyName" className="modern-label">
                          Företagsnamn *
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="companyName"
                          placeholder="Ange företagsnamn"
                          value={companyDetails.companyName}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, companyName: e.target.value })}
                        />
                      </div>

                      <div className="form-field half-width">
                        <label htmlFor="registrationNumber" className="modern-label">
                          Organisationsnummer *
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="registrationNumber"
                          placeholder="XXXXXX-XXXX"
                          value={companyDetails.registrationNumber}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, registrationNumber: e.target.value })}
                        />
                      </div>

                      <div className="form-field half-width">
                        <label htmlFor="vatNumber" className="modern-label">
                          Momsnummer
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="vatNumber"
                          placeholder="SE123456789001"
                          value={companyDetails.vatNumber}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, vatNumber: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Address Section */}
                <div className="form-section-wrapper">
                  <div className="section-header-external">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="section-heading-external">Adressinformation</h3>
                      <p className="section-description">Företagets postadress och kontaktuppgifter</p>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-fields">
                      <div className="form-field full-width">
                        <label htmlFor="address" className="modern-label">
                          Gatuadress *
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="address"
                          placeholder="Ange gatuadress"
                          value={companyDetails.address}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, address: e.target.value })}
                        />
                      </div>

                      <div className="form-field half-width">
                        <label htmlFor="postalCode" className="modern-label">
                          Postnummer *
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="postalCode"
                          placeholder="123 45"
                          value={companyDetails.postalCode}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, postalCode: e.target.value })}
                        />
                      </div>

                      <div className="form-field half-width">
                        <label htmlFor="city" className="modern-label">
                          Ort *
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="city"
                          placeholder="Ange ort"
                          value={companyDetails.city}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, city: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contact Information Section */}
                <div className="form-section-wrapper">
                  <div className="section-header-external">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="section-heading-external">Kontaktuppgifter</h3>
                      <p className="section-description">Telefon, e-post och kontaktpersoner</p>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-fields">
                      <div className="form-field half-width">
                        <label htmlFor="companyPhone" className="modern-label">
                          Telefonnummer
                        </label>
                        <input
                          type="tel"
                          className="modern-input"
                          id="companyPhone"
                          placeholder="08-123 456 78"
                          value={companyDetails.companyPhone}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, companyPhone: e.target.value })}
                        />
                      </div>

                      <div className="form-field half-width">
                        <label htmlFor="companyEmail" className="modern-label">
                          E-postadress
                        </label>
                        <input
                          type="email"
                          className="modern-input"
                          id="companyEmail"
                          placeholder="info@företag.se"
                          value={companyDetails.companyEmail}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, companyEmail: e.target.value })}
                        />
                      </div>

                      <div className="form-field half-width">
                        <label htmlFor="contactPerson" className="modern-label">
                          Kontaktperson
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="contactPerson"
                          placeholder="Ange kontaktperson"
                          value={companyDetails.contactPerson}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, contactPerson: e.target.value })}
                        />
                      </div>

                      <div className="form-field half-width">
                        <label htmlFor="contactEmail" className="modern-label">
                          Kontakt E-post
                        </label>
                        <input
                          type="email"
                          className="modern-input"
                          id="contactEmail"
                          placeholder="kontakt@företag.se"
                          value={companyDetails.contactEmail}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, contactEmail: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Banking Information Section */}
                <div className="form-section-wrapper">
                  <div className="section-header-external">
                    <div className="section-icon">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="1" y="3" width="15" height="13" />
                        <path d="m16 8 2-2 2 2" />
                        <path d="M21 14V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2" />
                        <path d="M7 12h5" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="section-heading-external">Betalningsinformation</h3>
                      <p className="section-description">Bankgiro och plusgiro för fakturabetalningar</p>
                    </div>
                  </div>

                  <div className="form-section">
                    <div className="form-fields">
                      <div className="form-field half-width">
                        <label htmlFor="bankgiro" className="modern-label">
                          Bankgiro
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="bankgiro"
                          placeholder="XXXX-XXXX"
                          value={companyDetails.bankgiro}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, bankgiro: e.target.value })}
                        />
                      </div>

                      <div className="form-field half-width">
                        <label htmlFor="plusgiro" className="modern-label">
                          Plusgiro
                        </label>
                        <input
                          type="text"
                          className="modern-input"
                          id="plusgiro"
                          placeholder="XXXXXX-X"
                          value={companyDetails.plusgiro}
                          onChange={(e) => setCompanyDetails({ ...companyDetails, plusgiro: e.target.value })}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Tab>

        {/* Users Tab */}
        <Tab
          eventKey="users"
          title={
            <span className="d-flex align-items-center tab-title">
              <div className="tab-icon-wrapper">
                <Users className="me-2" size={18} />
              </div>
              <span className="tab-text">Användare</span>
              <div className="tab-indicator"></div>
            </span>
          }
        >
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <Users size={24} />
                  Användarhantering
                </h2>
                <p className="section-subtitle">
                  Hantera teammedlemmar och deras behörigheter
                </p>
              </div>
              <div className="action-buttons">
                <Button
                  variant="primary"
                  className="d-flex align-items-center gap-2"
                  onClick={() => setShowInviteModal(true)}
                >
                  <PlusCircle size={16} />
                  Bjud in användare
                </Button>
                {/*<button
                  className="modern-button primary"
                  onClick={() => setShowInviteModal(true)}
                >
                  <Plus size={16} />
                  Bjud in användare
                </button>*/}
              </div>
            </div>

            <div className="modern-card-body">
              {/* Active Users Section */}
              <div className="user-section">
                <div className="section-header">
                  <h3 className="section-heading">
                    Aktiva användare ({associatedUsers.length})
                  </h3>
                </div>

                {associatedUsers.length > 0 ? (
                  <div className="user-grid">
                    {associatedUsers.map((user) => (
                      <div key={user.user_id} className="user-card">
                        <div className="user-avatar">
                          {user.first_name.charAt(0)}{user.last_name.charAt(0)}
                        </div>
                        <div className="user-info">
                          <h4 className="user-name">
                            {user.first_name} {user.last_name}
                          </h4>
                          <p className="user-email">{user.email}</p>
                          {user.phone_number && (
                            <p className="user-phone">{user.phone_number}</p>
                          )}
                          <div className="user-roles">
                            {user.roles.map((role, index) => (
                              <span key={index} className="role-badge">
                                {role}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="user-actions">
                          <button
                            className="action-btn edit"
                            onClick={() => handleOpenRoleModal(user)}
                            title="Ändra roller"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            className={`action-btn delete ${!canRemoveUser(user.user_id).canRemove ? 'disabled' : ''}`}
                            onClick={() => handleRemoveUser(user.user_id)}
                            title={
                              !canRemoveUser(user.user_id).canRemove
                                ? "Kan inte ta bort sista admin"
                                : "Ta bort användare"
                            }
                            disabled={!canRemoveUser(user.user_id).canRemove}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <Users size={48} className="empty-icon" />
                    <h4>Inga användare än</h4>
                    <p>Bjud in teammedlemmar för att komma igång</p>
                  </div>
                )}
              </div>

              {/* Pending Invitations Section */}
              <div className="user-section">
                <div className="section-header">
                  <h3 className="section-heading">
                    Väntande inbjudningar ({pendingInvitations.length})
                  </h3>
                </div>

                {pendingInvitations.length > 0 ? (
                  <>
                    <div className="invitation-list">
                      {pendingInvitations.map((invitation) => (
                        <div key={invitation.invitation_id} className="invitation-card">
                          <div className="invitation-avatar pending">
                            <Mail size={20} />
                          </div>
                          <div className="invitation-info">
                            <h4 className="invitation-email">{invitation.email}</h4>
                            <div className="invitation-meta">
                              <span className="meta-item">
                                Inbjuden: {new Date(invitation.created_at).toLocaleDateString('sv-SE')}
                              </span>
                              <span className="meta-item">
                                Utgår: {new Date(invitation.expires_at).toLocaleDateString('sv-SE')}
                              </span>
                            </div>
                            <div className="invitation-roles">
                              {invitation.roles.map((role, index) => (
                                <span key={index} className="role-badge pending">
                                  {role}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="invitation-actions">
                            <button
                              className="action-btn resend"
                              onClick={() => handleResendInvitation(invitation.invitation_id)}
                              title="Skicka igen"
                            >
                              <Send size={16} />
                            </button>
                            <button
                              className="action-btn delete"
                              onClick={() => handleDeleteInvitation(invitation.invitation_id)}
                              title="Ta bort inbjudan"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Add invite button after the list */}
                    <div className="d-flex justify-content-center mt-3">
                      <Button
                        variant="primary"
                        onClick={() => setShowInviteModal(true)}
                        className="d-flex align-items-center gap-2"
                      >
                        <PlusCircle size={16} />
                        Bjud in fler användare
                      </Button>

                      {/*<button
                        className="modern-button primary"
                        onClick={() => setShowInviteModal(true)}
                      >
                        <Plus size={16} />
                        Bjud in fler användare
                      </button>*/}
                    </div>
                  </>
                ) : (
                  <div className="empty-state small">
                    <Mail size={32} className="empty-icon" />
                    <h4>Inga väntande inbjudningar</h4>
                    <p>Bjud in teammedlemmar för att komma igång</p>
                    {/* Add invite button in empty state */}
                    <div className="mt-3">
                      <Button
                        variant="primary"
                        onClick={() => setShowInviteModal(true)}
                        className="d-flex align-items-center gap-2"
                      >
                        <PlusCircle size={16} />
                        Bjud in användare
                      </Button>
                      {/*  
                      <button
                        className="modern-button primary"
                        onClick={() => setShowInviteModal(true)}
                      >
                        <Plus size={16} />
                        Bjud in användare
                      </button>
                      */}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Tab>

        {/* Features Tab */}

        <Tab
          eventKey="features"
          title={
            <span className="d-flex align-items-center tab-title">
              <div className="tab-icon-wrapper">
                <Package className="me-2" size={18} />
              </div>
              <span className="tab-text">Funktioner</span>
              <div className="premium-indicator">
                <span className="premium-dot"></span>
              </div>
            </span>
          }
        >
          <div className="modern-card">
            <div className="modern-card-header">
              <div>
                <h2 className="section-title">
                  <Package size={24} />
                  Funktionshantering
                </h2>
                <p className="section-subtitle">
                  Aktivera eller inaktivera funktioner baserat på din prenumerationsplan
                </p>
              </div>
              <div className="action-buttons">
                <Button
                  variant="primary"
                  className="d-flex align-items-center gap-2"
                  onClick={saveFeatures}
                >
                  <Save className="me-2" size={16} />
                  Spara inställningar
                </Button>
                {/*<button
                  type="button"
                  className="modern-button primary"
                  onClick={saveFeatures}
                >
                  <Save size={16} />
                  Spara inställningar
                </button>*/}
              </div>
            </div>

            <div className="modern-card-body">
              <div className="features-grid">
                {/* Core Features */}
                <div className="feature-category">
                  <h3 className="category-title">Grundfunktioner</h3>
                  <div className="feature-cards">
                    <div className="feature-card">
                      <div className="feature-icon core">
                        <FileText size={24} />
                      </div>
                      <div className="feature-content">
                        <div className="feature-header">
                          <h4 className="feature-name">Fakturering</h4>
                          <div className="feature-toggle">
                            <input
                              className="toggle-input"
                              type="checkbox"
                              id="invoicingSwitch"
                              checked={features.invoicing}
                              onChange={() => updateFeature('invoicing', !features.invoicing)}
                            />
                            <label htmlFor="invoicingSwitch" className="toggle-label"></label>
                          </div>
                        </div>
                        <p className="feature-description">
                          Skapa och hantera fakturor, offerter och kreditnotor
                        </p>
                        <div className="feature-benefits">
                          <span className="benefit-tag">✓ Automatisk numrering</span>
                          <span className="benefit-tag">✓ PDF-generering</span>
                          <span className="benefit-tag">✓ E-postutskick</span>
                        </div>
                      </div>
                    </div>

                    <div className="feature-card">
                      <div className="feature-icon core">
                        <Package size={24} />
                      </div>
                      <div className="feature-content">
                        <div className="feature-header">
                          <h4 className="feature-name">Utläggshantering</h4>
                          <div className="feature-toggle">
                            <input
                              className="toggle-input"
                              type="checkbox"
                              id="expensesSwitch"
                              checked={features.expense_management}
                              onChange={() => updateFeature('expense_management', !features.expense_management)}
                            />
                            <label htmlFor="expensesSwitch" className="toggle-label"></label>
                          </div>
                        </div>
                        <p className="feature-description">
                          Hantera utlägg, kvitton och återbetalningar
                        </p>
                        <div className="feature-benefits">
                          <span className="benefit-tag">✓ Kvittoscanning</span>
                          <span className="benefit-tag">✓ Kategorisering</span>
                          <span className="benefit-tag">✓ Rapporter</span>
                        </div>
                      </div>
                    </div>

                    <div className="feature-card">
                      <div className="feature-icon core">
                        <FileText size={24} />
                      </div>
                      <div className="feature-content">
                        <div className="feature-header">
                          <h4 className="feature-name">Dokumenthantering</h4>
                          <div className="feature-toggle">
                            <input
                              className="toggle-input"
                              type="checkbox"
                              id="documentManagementSwitch"
                              checked={features.document_management}
                              onChange={() => updateFeature('document_management', !features.document_management)}
                            />
                            <label htmlFor="documentManagementSwitch" className="toggle-label"></label>
                          </div>
                        </div>
                        <p className="feature-description">
                          Organisera och hantera alla dina dokument och filer
                        </p>
                        <div className="feature-benefits">
                          <span className="benefit-tag">✓ Molnlagring</span>
                          <span className="benefit-tag">✓ Sökfunktion</span>
                          <span className="benefit-tag">✓ Versionskontroll</span>
                        </div>
                      </div>
                    </div>

                    <div className="feature-card">
                      <div className="feature-icon core">
                        <Users size={24} />
                      </div>
                      <div className="feature-content">
                        <div className="feature-header">
                          <h4 className="feature-name">Tidsrapportering</h4>
                          <div className="feature-toggle">
                            <input
                              className="toggle-input"
                              type="checkbox"
                              id="timeTrackingSwitch"
                              checked={features.time_tracking}
                              onChange={() => updateFeature('time_tracking', !features.time_tracking)}
                            />
                            <label htmlFor="timeTrackingSwitch" className="toggle-label"></label>
                          </div>
                        </div>
                        <p className="feature-description">
                          Spåra arbetstid och generera timrapporter
                        </p>
                        <div className="feature-benefits">
                          <span className="benefit-tag">✓ Tidregistrering</span>
                          <span className="benefit-tag">✓ Projektspårning</span>
                          <span className="benefit-tag">✓ Rapporter</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Premium Features */}
                <div className="feature-category">
                  <div className="category-header">
                    <h3 className="category-title">Premium-funktioner</h3>
                    <span className="premium-badge">Premium</span>
                  </div>
                  <div className="feature-cards">
                    <div className="feature-card premium">
                      <div className="feature-icon premium">
                        <Building size={24} />
                      </div>
                      <div className="feature-content">
                        <div className="feature-header">
                          <h4 className="feature-name">Bankkoppling</h4>
                          <div className="feature-toggle">
                            <input
                              className="toggle-input"
                              type="checkbox"
                              id="bankingSwitch"
                              checked={features.banking_integration}
                              onChange={() => updateFeature('banking_integration', !features.banking_integration)}
                            />
                            <label htmlFor="bankingSwitch" className="toggle-label premium"></label>
                          </div>
                        </div>
                        <p className="feature-description">
                          Automatisk synkronisering med din bankdata
                        </p>
                        <div className="feature-benefits">
                          <span className="benefit-tag">✓ Automatisk import</span>
                          <span className="benefit-tag">✓ Kontomatchning</span>
                          <span className="benefit-tag">✓ Realtidsuppdatering</span>
                        </div>
                      </div>
                    </div>

                    <div className="feature-card premium">
                      <div className="feature-icon premium">
                        <Users size={24} />
                      </div>
                      <div className="feature-content">
                        <div className="feature-header">
                          <h4 className="feature-name">Lönehantering</h4>
                          <div className="feature-toggle">
                            <input
                              className="toggle-input"
                              type="checkbox"
                              id="payrollSwitch"
                              checked={features.payroll}
                              onChange={() => updateFeature('payroll', !features.payroll)}
                            />
                            <label htmlFor="payrollSwitch" className="toggle-label premium"></label>
                          </div>
                        </div>
                        <p className="feature-description">
                          Komplett lönehantering för dina anställda
                        </p>
                        <div className="feature-benefits">
                          <span className="benefit-tag">✓ Löneberäkning</span>
                          <span className="benefit-tag">✓ Skattehantering</span>
                          <span className="benefit-tag">✓ Lönespecifikationer</span>
                        </div>
                      </div>
                    </div>

                    <div className="feature-card premium">
                      <div className="feature-icon premium">
                        <Package size={24} />
                      </div>
                      <div className="feature-content">
                        <div className="feature-header">
                          <h4 className="feature-name">Multivaluta</h4>
                          <div className="feature-toggle">
                            <input
                              className="toggle-input"
                              type="checkbox"
                              id="multiCurrencySwitch"
                              checked={features.multi_currency}
                              onChange={() => updateFeature('multi_currency', !features.multi_currency)}
                            />
                            <label htmlFor="multiCurrencySwitch" className="toggle-label premium"></label>
                          </div>
                        </div>
                        <p className="feature-description">
                          Hantera flera valutor och automatiska växelkurser
                        </p>
                        <div className="feature-benefits">
                          <span className="benefit-tag">✓ Flera valutor</span>
                          <span className="benefit-tag">✓ Växelkurser</span>
                          <span className="benefit-tag">✓ Valutaomräkning</span>
                        </div>
                      </div>
                    </div>

                    <div className="feature-card premium">
                      <div className="feature-icon premium">
                        <Settings size={24} />
                      </div>
                      <div className="feature-content">
                        <div className="feature-header">
                          <h4 className="feature-name">API-åtkomst</h4>
                          <div className="feature-toggle">
                            <input
                              className="toggle-input"
                              type="checkbox"
                              id="apiAccessSwitch"
                              checked={features.api_access}
                              onChange={() => updateFeature('api_access', !features.api_access)}
                            />
                            <label htmlFor="apiAccessSwitch" className="toggle-label premium"></label>
                          </div>
                        </div>
                        <p className="feature-description">
                          Integrera med externa system via vårt API
                        </p>
                        <div className="feature-benefits">
                          <span className="benefit-tag">✓ REST API</span>
                          <span className="benefit-tag">✓ Webhooks</span>
                          <span className="benefit-tag">✓ Dokumentation</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Tab>

      </Tabs>

      <RoleModal
        show={showRoleModal}
        handleClose={handleCloseRoleModal}
        user={selectedUser}
        allRoles={allRoles}
        handleSaveRoles={handleSaveRoles}
      />

      <InviteUserModal
        show={showInviteModal}
        handleClose={() => setShowInviteModal(false)}
        allRoles={allRoles}
        handleInviteUser={handleInviteUser}
      />

      {sieVerificationData && (
        <SieVerificationModal
          show={showSieVerificationModal}
          onHide={() => setShowSieVerificationModal(false)}
          verificationData={sieVerificationData}
          onConfirmImport={handleConfirmImport}
          onCancel={handleCancelImport}
          isImporting={isImporting}
        />
      )}

      <SieExportModal
        show={showSieExportModal}
        onHide={handleCloseExportModal}
        fiscalYears={fiscalYears}
        companyId={companyId}
      />

      <CreateFiscalYearModal show={showCreateFiscalYearModal} handleClose={handleCloseModal} handleSave={handleSaveFiscalYear} />

    </>
  );
};

export default CompanySettings;